import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import BottomNavBar from '../components/BottomNavBar';
import SEOMeta from '../components/SEOMeta';
import { useAuth } from '../contexts/AuthContext';

/* ── Types ── */

interface Payment {
  _id: string;
  orderId: string;
  packageType: 'monthly' | 'yearly';
  baseAmount: number;
  discount: number;
  totalAmount: number;
  voucherCode?: string;
  method: 'bank_transfer' | 'admin';
  status: 'pending' | 'confirmed' | 'cancelled' | 'expired';
  createdAt: string;
  confirmedAt?: string;
}

/* ── Styled ── */

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  padding-bottom: 96px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #f1f5f9;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #2DBCAF;
  padding: 0;
  .material-symbols-outlined { font-size: 24px; }
`;

const HeaderTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  flex: 1;
`;

/* Summary Card */
const SummaryCard = styled.div`
  margin: 20px 16px 0;
  background: linear-gradient(135deg, #2DBCAF, #1fa89c);
  border-radius: 20px;
  padding: 22px 20px;
  color: white;
  box-shadow: 0 8px 28px rgba(45,188,175,0.3);
`;

const SummaryTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SummaryLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  opacity: 0.8;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SummaryIcon = styled.div`
  background: rgba(255,255,255,0.2);
  border-radius: 10px;
  padding: 8px;
  .material-symbols-outlined {
    font-size: 22px;
    display: block;
    font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }
`;

const StatusActive = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.2);
  border-radius: 9999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
  .dot { width: 7px; height: 7px; background: #a7f3d0; border-radius: 50%; }
`;

const PlanName = styled.p`
  font-size: 20px;
  font-weight: 800;
  margin: 0 0 4px;
`;

const PlanExpiry = styled.p`
  font-size: 12px;
  opacity: 0.75;
  margin: 0;
`;

/* Section */
const SectionTitle = styled.div`
  padding: 24px 16px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  h3 {
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
    margin: 0;
  }
`;

const TotalBadge = styled.span`
  background: rgba(45,188,175,0.1);
  color: #2DBCAF;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 9999px;
`;

/* List */
const ListWrapper = styled.div`
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PayCard = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const PayIconBox = styled.div<{ color: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${p => p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  .material-symbols-outlined {
    font-size: 22px;
    color: white;
    font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }
`;

const PayInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PayPlan = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 3px;
`;

const PayDate = styled.p`
  font-size: 11px;
  color: #94a3b8;
  margin: 0;
`;

const PayRight = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const PayAmount = styled.p`
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 4px;
`;

const StatusBadge = styled.span<{ status: string }>`
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 9999px;
  background: ${p =>
    p.status === 'success' ? 'rgba(34,197,94,0.1)' :
    p.status === 'pending' ? 'rgba(234,179,8,0.1)' :
    'rgba(239,68,68,0.1)'};
  color: ${p =>
    p.status === 'success' ? '#16a34a' :
    p.status === 'pending' ? '#ca8a04' :
    '#dc2626'};
`;

/* Empty */
const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 32px;
  text-align: center;
  gap: 10px;
  .material-symbols-outlined {
    font-size: 52px;
    color: #cbd5e1;
    font-variation-settings: 'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 48;
  }
  h3 { font-size: 15px; font-weight: 700; color: #475569; margin: 0; }
  p  { font-size: 13px; color: #94a3b8; margin: 0; line-height: 1.5; }
`;

const PremiumBtn = styled.button`
  margin-top: 8px;
  padding: 12px 28px;
  background: #FFA929;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(255,169,41,0.3);
  &:active { transform: scale(0.97); }
`;

/* Login */
const LoginBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 32px;
  text-align: center;
  gap: 12px;
  .material-symbols-outlined {
    font-size: 56px;
    color: #2DBCAF;
    font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 48;
  }
  h3 { font-size: 16px; font-weight: 700; color: #475569; margin: 0; }
  p  { font-size: 13px; color: #94a3b8; margin: 0; line-height: 1.5; }
`;

const LoginBtn = styled.button`
  margin-top: 8px;
  padding: 12px 28px;
  background: #2DBCAF;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(45,188,175,0.3);
`;

/* ── Helpers ── */

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function formatRupiah(n: number) {
  return 'Rp ' + n.toLocaleString('id-ID');
}

const PLAN_LABEL: Record<string, string> = {
  monthly: 'Premium 1 Bulan',
  yearly: 'Premium Setahun',
  fouryear: 'Premium 4 Tahun',
};

const STATUS_LABEL: Record<string, string> = {
  confirmed: 'Berhasil',
  pending: 'Diproses',
  cancelled: 'Dibatalkan',
  expired: 'Kadaluarsa',
};

const METHOD_LABEL: Record<string, string> = {
  bank_transfer: 'Transfer Bank',
  admin: 'Aktivasi Admin',
};

/* ── Component ── */

export default function PaymentHistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Check active subscription from user
  const hasActiveSub = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetch('/api/payments', { credentials: 'include' })
      .then(r => r.ok ? r.json() : { payments: [] })
      .then((data) => {
        const list = Array.isArray(data.payments) ? data.payments : [];
        setPayments(list);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <>
      <SEOMeta
        title="Riwayat Pembayaran - KinderGame.id"
        description="Riwayat transaksi dan langganan premium KinderGame.id"
        path="/payments"
      />
      <PageWrapper>
        <Header>
          <BackBtn onClick={() => navigate(-1)}>
            <span className="material-symbols-outlined">arrow_back</span>
          </BackBtn>
          <HeaderTitle>Riwayat Pembayaran</HeaderTitle>
        </Header>

        {!user ? (
          <LoginBox>
            <span className="material-symbols-outlined">lock</span>
            <h3>Masuk untuk melihat riwayat</h3>
            <p>Login dengan Google untuk melihat riwayat pembayaran kamu.</p>
            <LoginBtn onClick={() => { const returnTo = encodeURIComponent(window.location.pathname + window.location.search); window.location.href = '/api/auth/google?returnTo=' + returnTo; }}>
              Login dengan Google
            </LoginBtn>
          </LoginBox>
        ) : (
          <>
            {/* Active subscription card */}
            {hasActiveSub && user?.subscription && (
              <SummaryCard>
                <SummaryTop>
                  <SummaryLabel>Langganan Aktif</SummaryLabel>
                  <SummaryIcon>
                    <span className="material-symbols-outlined">workspace_premium</span>
                  </SummaryIcon>
                </SummaryTop>
                <StatusActive>
                  <div className="dot" />
                  Aktif
                </StatusActive>
                <PlanName>{PLAN_LABEL[user.subscription.type || ''] ?? 'Premium'}</PlanName>
                <PlanExpiry>Berlaku hingga {formatDate(user.subscription.endDate || '')}</PlanExpiry>
              </SummaryCard>
            )}

            <SectionTitle>
              <h3>Riwayat Transaksi</h3>
              {payments.length > 0 && (
                <TotalBadge>{payments.length} transaksi</TotalBadge>
              )}
            </SectionTitle>

            {loading ? (
              <EmptyBox>
                <span className="material-symbols-outlined">hourglass_top</span>
                <h3>Memuat...</h3>
              </EmptyBox>
            ) : payments.length === 0 ? (
              <EmptyBox>
                <span className="material-symbols-outlined">receipt_long</span>
                <h3>Belum ada transaksi</h3>
                <p>Kamu belum pernah berlangganan Premium. Mulai sekarang dan buka akses penuh!</p>
                <PremiumBtn onClick={() => navigate('/premium')}>
                  Lihat Paket Premium
                </PremiumBtn>
              </EmptyBox>
            ) : (
              <ListWrapper>
                {payments.map(pay => (
                  <PayCard key={pay._id}>
                    <PayIconBox
                      color={
                        pay.status === 'confirmed' ? '#2DBCAF' :
                        pay.status === 'pending' ? '#EAB308' : '#EF4444'
                      }
                    >
                      <span className="material-symbols-outlined">
                        {pay.method === 'admin' ? 'admin_panel_settings' : 
                         pay.packageType === 'yearly' ? 'star' : 'workspace_premium'}
                      </span>
                    </PayIconBox>

                    <PayInfo>
                      <PayPlan>{PLAN_LABEL[pay.packageType] ?? pay.packageType}</PayPlan>
                      <PayDate>
                        {formatDate(pay.createdAt)}
                        {pay.method === 'admin' && ' • Aktivasi Admin'}
                      </PayDate>
                    </PayInfo>

                    <PayRight>
                      <PayAmount>{formatRupiah(pay.totalAmount)}</PayAmount>
                      <StatusBadge status={pay.status}>
                        {STATUS_LABEL[pay.status] ?? pay.status}
                      </StatusBadge>
                    </PayRight>
                  </PayCard>
                ))}
              </ListWrapper>
            )}
          </>
        )}
      </PageWrapper>
      <BottomNavBar />
    </>
  );
}
